/**
 * Guardian's wandering watch and weekly alarm noise (V8 D6, V9 L4255-4296 and its logic at
 * L42866-42915). Neither has a feed yet: door sensors and resident tags come in through the
 * sensor connection and Junction once a building connects them, and the alarm report needs
 * the building's alarm and nurse-call events. Until then each card says so in plain words:
 * no sample residents, doors, drills or counts are drawn as if they were real.
 *
 * Who sees them (design): the wandering watch for Care Staff, the Facility Admin and
 * Aithora's team; the alarm report for the Facility Admin and Aithora's team. Read-only roles
 * get no Send, Recheck now, Rate now or Schedule drill (there is nothing to act on yet anyway).
 */
import type { Lens } from './model';

/** What a connected wandering feed will carry (typed now so the card can be wired to it). */
export interface WanderingFeed {
  atRisk: Array<{ residentId: string; name: string; room: string | null; pattern: string; tag: string; risk: string }>;
  doors: Array<{ name: string; state: string; alert: boolean }>;
  drills: Array<{ shift: string; last: string | null; overdue: boolean }>;
}

/** What a connected alarm report will carry. */
export interface AlarmNoiseFeed {
  period: string;
  stats: Array<{ v: string; k: string }>;
  sources: Array<{ k: string; why: string; fix: string; who: string }>;
}

export interface FeedsInput {
  lens: Lens;
  roleLabel: string;
  wandering?: WanderingFeed | null;
  alarms?: AlarmNoiseFeed | null;
}

export function guardianFeeds(inp: FeedsInput) {
  const staff = inp.lens === 'staff';
  const wd = inp.wandering ?? null;
  const nz = inp.alarms ?? null;
  return {
    wdShow: true,
    wdSum: wd ? wd.atRisk.length + ' residents at risk · ' + wd.doors.length + ' exit doors' : 'Not connected yet',
    wdTag: wd ? '' : 'NOT CONNECTED',
    wdConnected: !!wd,
    wdLive: false,
    wdClaimed: false,
    wdSearch: false,
    wdReview: false,
    wdRes: [],
    wdDoors: (wd?.doors ?? []).map((d) => ({ k: d.name, v: d.state, style: 'font-size:16px;line-height:1.4;color:' + (d.alert ? '#B3261E;font-weight:700;' : '#22272E;') })),
    wdDrills: (wd?.drills ?? []).map((d) => ({ k: d.shift, v: d.last ?? 'No drill in the last 12 months', style: 'flex:1 1 200px;min-width:0;font-size:16px;line-height:1.4;color:' + (d.overdue ? '#B3261E;font-weight:700;' : '#22272E;'), hasAct: false })),
    wdNone: wd ? '' : 'Door sensors and resident tags are not connected for this building yet. Residents at risk of wandering, exit doors and missing-resident drills show here once they are.',
    wdFoot: 'Runs on your existing door sensors and tags; no new hardware. Door sensors come in through the sensor connection, watch zone alerts through Junction. Same hand-off ladder as every Guardian alert.',
    nzShow: !staff,
    nzSum: nz ? nz.period : 'Not connected yet',
    nzTag: nz ? '' : 'NOT CONNECTED',
    nzConnected: !!nz,
    nzStats: (nz?.stats ?? []).map((x) => ({ v: x.v, k: x.k, style: 'font-size:22px;font-weight:750;letter-spacing:-.02em;line-height:1;font-variant-numeric:tabular-nums;color:#22272E;' })),
    nzRows: (nz?.sources ?? []).map((r) => ({ k: r.k, why: r.why, fix: r.fix, who: r.who, todo: false, done: inp.lens === 'ro', doneT: 'Read-only as ' + inp.roleLabel + ' · the facility admin decides' })),
    nzNone: nz ? '' : 'The weekly alarm report needs the building’s alarm and nurse-call events in Aithora. Alerts, how many needed a person, answer times and the noisiest sources show here once they are connected.',
    nzFoot: 'Too many alarms that need no one make staff slower to answer the ones that do. Each fix keeps every alarm that needs a person.',
  };
}
